import { useState } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import CloseIcon from "../ui/icons/CloseIcon";
import SearchIcon from "../ui/icons/SearchIcon";
import { Text } from "../ui/text";
import ReportDownloadButton from "./report-download-button";
import ReactMarkdown from "react-markdown";
import { Reference } from "../message/message-list";
import dayjs from "dayjs";
import remarkGfm from "remark-gfm";
import React from "react";

const ReportPreviewDialog = ({
  content,
  references,
  createdAt,
  onOpenChange,
}: {
  content: string;
  references: Reference[];
  createdAt: string;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const getTitle = (content: string) => {
    const h1Match = content.match(/^#\s+(.+)$/m);
    const h1Title = h1Match ? h1Match[1] : null;
    return h1Title;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          className="border border-primary-80 rounded-[8px] px-4 py-[10px]"
          style={{
            background:
              "linear-gradient(144deg, #ECFCFA 13.78%, #FEF9F7 49.93%, #FFF 86.08%)",
          }}
        >
          <SearchIcon color="#333333" />
          <Text variant="body-regular">Preview</Text>
        </Button>
      </DialogTrigger>

      <DialogContent className="bg-white/95 w-[800px] max-h-[90vh] h-[1016px] pl-[56px] pr-[24px] pt-2 rounded-[10px] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex justify-between items-center">
            <DialogTitle>
              <Text variant="title-xl">Report Preview</Text>
            </DialogTitle>
            <div className="flex gap-10 items-center">
              <div className="flex gap-2 pt-5">
                <ReportDownloadButton
                  type="pdf"
                  content={content}
                  references={references}
                  createdAt={createdAt}
                />
                <ReportDownloadButton
                  type="word"
                  content={content}
                  references={references}
                  createdAt={createdAt}
                />
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-[40px] h-[40px] flex justify-center items-center"
              >
                <CloseIcon color="#333333" />
              </button>
            </div>
          </div>
        </DialogHeader>
        <div className="relative overflow-y-auto flex-1 min-h-0">
          <div className="bg-white sticky top-[-1px] flex flex-col gap-[8px] border-b border-grey-80 pb-[16px] pt-[36px]">
            <Text variant="headline-sm">{getTitle(content)}</Text>
            <Text variant={"title-sm"} className="text-grey-40">
              {dayjs(createdAt).format("YYYY.MM.DD")}
            </Text>
          </div>
          <div className="prose prose-slate max-w-none">
            <ReactMarkdown
              components={{
                h1: ({ children }) => <></>,
                h2: ({ children }) => (
                  <div className="pt-[48px]">
                    <Text variant={"title-xl"}>{children}</Text>
                  </div>
                ),
                h3: ({ children }) => (
                  <div className="pt-[48px] pb-2">
                    <Text variant={"title-lg"}>{children}</Text>
                  </div>
                ),
                h4: ({ children }) => (
                  <Text variant={"title-md"}>{children}</Text>
                ),
                h5: ({ children }) => (
                  <Text variant={"title-sm"}>{children}</Text>
                ),
                p: ({ children }) => (
                  <Text variant={"body-lg"}>{children}</Text>
                ),
                ul: ({ children }) => (
                  <ul className="mb-2 list-disc pl-4">{children}</ul>
                ),
                li: ({ children }) => <li className="mb-2 ">{children}</li>,
                hr: () => <hr className="my-4 border-t border-gray-300" />,
                td: ({ children }: { children?: React.ReactNode }) => {
                  // 1단계: 먼저 줄바꿈 처리
                  const withLineBreaks = React.Children.map(
                    children,
                    (child) => {
                      if (typeof child === "string") {
                        const parts = child
                          .split(/(?=• )/g)
                          .filter((part) => part.trim());

                        if (parts.length > 1) {
                          return parts.map((part, index) => (
                            <React.Fragment key={index}>
                              {index > 0 && <br />}
                              {part}
                            </React.Fragment>
                          ));
                        }
                      }
                      return child;
                    }
                  );

                  return <td>{withLineBreaks}</td>;
                },
              }}
              remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
            >
              {content}
            </ReactMarkdown>
            {(references?.length ?? 0) > 0 && <div className="mt-[48px]">
              <Text variant="title-xl">References</Text>
              <ol>
                {references.map((reference, index) => {
                  const isPdf = reference.title?.toLowerCase().endsWith(".pdf");
                  return (
                    <li key={index}>
                      {isPdf ? (
                        <span>{reference.title}</span>
                      ) : (
                        <a href={reference.url} target="_blank" rel="noopener noreferrer">
                          {reference.title}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportPreviewDialog;
